import { forwardRef, useId } from 'react';
import { motion } from 'motion/react';
import { cn } from './cn';

export type SwitchSize = 'sm' | 'md';

export interface SwitchProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'onChange' | 'children'
  > {
  /** Controlled checked state. */
  checked: boolean;
  /** Called with the next checked value when toggled. */
  onChange: (checked: boolean) => void;
  /** Label rendered to the right of the track. Clicking it toggles. */
  label?: React.ReactNode;
  /** Secondary description under the label. */
  description?: React.ReactNode;
  disabled?: boolean;
  size?: SwitchSize;
  /** Class applied to the outer wrapper (track + label). */
  className?: string;
}

const sizeStyles: Record<
  SwitchSize,
  { track: string; thumb: string; offX: number; onX: number }
> = {
  // w-11 (44) h-6 (24): 20px thumb, 2px inset → travel 44 - 20 - 4 = 20
  md: { track: 'w-11 h-6 p-0.5', thumb: 'w-5 h-5', offX: 0, onX: 20 },
  // w-9 (36) h-5 (20): 16px thumb, 2px inset → travel 36 - 16 - 4 = 16
  sm: { track: 'w-9 h-5 p-0.5', thumb: 'w-4 h-4', offX: 0, onX: 16 },
};

const spring = { type: 'spring' as const, stiffness: 500, damping: 30 };

/**
 * Toggle switch — replaces MUI <Switch />.
 * role="switch" button with animated thumb (spring x). Space/Enter toggle.
 * Optional label/description to the right; clicking them toggles too.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      onChange,
      label,
      description,
      disabled = false,
      size = 'md',
      className,
      id,
      ...props
    },
    ref,
  ) => {
    const reactId = useId();
    const switchId = id ?? reactId;
    const labelId = label ? `${switchId}-label` : undefined;
    const descId = description ? `${switchId}-desc` : undefined;
    const s = sizeStyles[size];

    const toggle = () => {
      if (disabled) return;
      onChange(!checked);
    };

    return (
      <div
        className={cn(
          'flex items-center gap-3',
          disabled && 'opacity-60',
          className,
        )}
      >
        <button
          ref={ref}
          type="button"
          id={switchId}
          role="switch"
          aria-checked={checked}
          aria-labelledby={labelId}
          aria-describedby={descId}
          disabled={disabled}
          onClick={toggle}
          className={cn(
            'relative shrink-0 inline-flex items-center rounded-full border ' +
              'transition-colors duration-200 select-none ' +
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
              'focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ' +
              'disabled:cursor-not-allowed',
            s.track,
            checked ? 'bg-primary border-primary' : 'bg-surface-2 border-line',
          )}
          {...props}
        >
          <motion.span
            className={cn('block rounded-full bg-white shadow-sm', s.thumb)}
            initial={false}
            animate={{ x: checked ? s.onX : s.offX }}
            transition={spring}
          />
        </button>

        {(label || description) && (
          <div
            className="flex min-w-0 flex-col cursor-pointer"
            onClick={toggle}
          >
            {label && (
              <span
                id={labelId}
                className="text-sm font-medium text-content select-none"
              >
                {label}
              </span>
            )}
            {description && (
              <span id={descId} className="text-xs text-muted select-none">
                {description}
              </span>
            )}
          </div>
        )}
      </div>
    );
  },
);

Switch.displayName = 'Switch';
